import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Eye, Pencil, Hammer } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select, Textarea } from '../components/ui/Input.tsx';
import { TemplateStatusBadge, ChannelBadge } from '../components/ui/Badge.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { Card } from '../components/ui/Card.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

interface Template {
  id: string;
  name: string;
  channel: 'email' | 'sms' | 'push' | 'webhook';
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  variables: string[];
  status: 'draft' | 'active' | 'archived';
  createdAt: string;
}

type TemplateForm = {
  name: string;
  channel: Template['channel'];
  subject: string;
  bodyText: string;
  bodyHtml: string;
};

const EMPTY_FORM: TemplateForm = {
  name: '',
  channel: 'email',
  subject: '',
  bodyText: '',
  bodyHtml: '',
};

export function TemplatesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const [showCreate, setShowCreate] = useState(false);
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [previewVars, setPreviewVars] = useState<Record<string, string>>({});
  const [archiveTarget, setArchiveTarget] = useState<Template | null>(null);
  const [form, setForm] = useState<TemplateForm>(EMPTY_FORM);
  const [channelFilter, setChannelFilter] = useState('');

  const { data: templates = [], isLoading } = useQuery<Template[]>({
    queryKey: ['templates'],
    queryFn: () => api.get('/api/v1/templates').then((r) => r.data),
  });

  // Filtered view
  const visible = channelFilter ? templates.filter((t) => t.channel === channelFilter) : templates;
  const active = visible.filter((t) => t.status !== 'archived');
  const archived = visible.filter((t) => t.status === 'archived');

  // ── mutations ────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: TemplateForm) => api.post('/api/v1/templates', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      toast.success('Template created');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create template')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TemplateForm> }) =>
      api.patch(`/api/v1/templates/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      setEditTemplate(null);
      toast.success('Template updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update template')),
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/templates/${id}/activate`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      toast.success('Template activated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      setArchiveTarget(null);
      toast.success('Template archived');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── helpers ──────────────────────────────────────────────────────────────────

  function openEdit(t: Template) {
    setForm({
      name: t.name,
      channel: t.channel,
      subject: t.subject ?? '',
      bodyText: t.bodyText ?? '',
      bodyHtml: t.bodyHtml ?? '',
    });
    setEditTemplate(t);
  }

  function openPreview(t: Template) {
    const vars: Record<string, string> = {};
    (t.variables ?? []).forEach((v) => {
      vars[v] = `{{${v}}}`;
    });
    setPreviewVars(vars);
    setPreviewTemplate(t);
  }

  function renderPreview(text: string, vars: Record<string, string>) {
    let out = text;
    Object.entries(vars).forEach(([k, v]) => {
      out = out.replaceAll(`{{${k}}}`, v || `[${k}]`);
    });
    return out;
  }

  return (
    <div>
      <PageHeader
        title="Templates"
        description="Message templates for campaigns and automations"
        action={
          <Button
            onClick={() => {
              setForm(EMPTY_FORM);
              setShowCreate(true);
            }}
            size="sm"
          >
            + New template
          </Button>
        }
      />

      {/* Channel filter */}
      <div className="flex items-center gap-1 mb-4 border-b border-zinc-100 pb-0">
        {(['', 'email', 'sms', 'push', 'webhook'] as const).map((ch) => (
          <button
            key={ch}
            onClick={() => setChannelFilter(ch)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
              channelFilter === ch
                ? 'border-zinc-900 text-zinc-900'
                : 'border-transparent text-zinc-400 hover:text-zinc-700'
            }`}
          >
            {ch || 'All'}
            <span
              className={`ml-1.5 text-xs tabular-nums ${channelFilter === ch ? 'text-zinc-500' : 'text-zinc-300'}`}
            >
              {ch
                ? templates.filter((t) => t.channel === ch && t.status !== 'archived').length
                : templates.filter((t) => t.status !== 'archived').length}
            </span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-36 bg-white border border-zinc-200 rounded-xl animate-pulse"
            />
          ))}
        </div>
      ) : active.length === 0 ? (
        <EmptyState
          icon={<FileText size={28} />}
          title="No templates yet"
          description="Create email, SMS or push templates used in campaigns and automations."
          action={{
            label: '+ New template',
            onClick: () => {
              setForm(EMPTY_FORM);
              setShowCreate(true);
            },
          }}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {active.map((t) => (
            <Card key={t.id} padding="md" className="flex flex-col justify-between gap-3">
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <ChannelBadge channel={t.channel} />
                  <TemplateStatusBadge status={t.status} />
                </div>
                <h3 className="text-sm font-medium text-zinc-900 leading-snug">{t.name}</h3>
                {t.subject && (
                  <p className="mt-1 text-xs text-zinc-500 truncate" title={t.subject}>
                    {t.subject}
                  </p>
                )}
                {t.bodyText && (
                  <p className="mt-1 text-xs text-zinc-400 line-clamp-2 font-mono leading-relaxed">
                    {t.bodyText.slice(0, 120)}
                    {t.bodyText.length > 120 ? '…' : ''}
                  </p>
                )}
                {t.variables && t.variables.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.variables.slice(0, 4).map((v) => (
                      <code
                        key={v}
                        className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded font-mono"
                      >
                        {'{{' + v + '}}'}
                      </code>
                    ))}
                    {t.variables.length > 4 && (
                      <span className="text-[10px] text-zinc-400">+{t.variables.length - 4}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 pt-2 border-t border-zinc-50 flex-wrap">
                <button
                  onClick={() => openPreview(t)}
                  className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors"
                  title="Preview"
                >
                  <Eye size={13} />
                </button>
                <button
                  onClick={() => navigate(`/templates/${t.id}/edit`)}
                  className="p-1.5 text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                  title="Open builder"
                >
                  <Hammer size={13} />
                </button>
                <button
                  onClick={() => openEdit(t)}
                  className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors"
                  title="Quick edit"
                >
                  <Pencil size={13} />
                </button>
                {t.status === 'draft' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={activateMutation.isPending}
                    onClick={() => activateMutation.mutate(t.id)}
                  >
                    Activate
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setArchiveTarget(t)}>
                  Archive
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <details className="mt-6">
          <summary className="text-xs text-zinc-400 cursor-pointer select-none hover:text-zinc-600 transition-colors">
            {archived.length} archived template{archived.length !== 1 ? 's' : ''}
          </summary>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 opacity-50">
            {archived.map((t) => (
              <Card key={t.id} padding="md" className="flex items-center gap-3">
                <ChannelBadge channel={t.channel} />
                <span className="text-sm text-zinc-500 line-through truncate">{t.name}</span>
              </Card>
            ))}
          </div>
        </details>
      )}

      {/* Create Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Template" size="lg">
        <TemplateFormFields form={form} setForm={setForm} />
        {createMutation.isError && (
          <p className="text-xs text-red-500 mt-3">{extractError(createMutation.error)}</p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={createMutation.isPending}
            disabled={!form.name || !form.bodyText}
            onClick={() => createMutation.mutate(form)}
          >
            Create template
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editTemplate}
        onClose={() => setEditTemplate(null)}
        title={`Edit: ${editTemplate?.name}`}
        size="lg"
      >
        <TemplateFormFields form={form} setForm={setForm} lockChannel />
        {updateMutation.isError && (
          <p className="text-xs text-red-500 mt-3">{extractError(updateMutation.error)}</p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setEditTemplate(null)}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={updateMutation.isPending}
            disabled={!form.name}
            onClick={() =>
              editTemplate && updateMutation.mutate({ id: editTemplate.id, data: form })
            }
          >
            Save changes
          </Button>
        </ModalFooter>
      </Modal>

      {/* Preview Modal */}
      <Modal
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        title={`Preview: ${previewTemplate?.name}`}
        size="lg"
      >
        {previewTemplate && (
          <div className="space-y-4">
            {previewTemplate.variables && previewTemplate.variables.length > 0 && (
              <div>
                <p className="text-xs font-medium text-zinc-500 mb-2">Test variables</p>
                <div className="grid grid-cols-2 gap-2">
                  {previewTemplate.variables.map((v) => (
                    <Input
                      key={v}
                      label={v}
                      value={previewVars[v] ?? ''}
                      onChange={(e) => setPreviewVars((prev) => ({ ...prev, [v]: e.target.value }))}
                      placeholder={`value for ${v}`}
                    />
                  ))}
                </div>
              </div>
            )}
            {previewTemplate.subject && (
              <div>
                <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-wide mb-1">
                  Subject
                </p>
                <p className="text-sm text-zinc-800 bg-zinc-50 rounded-lg px-3 py-2">
                  {renderPreview(previewTemplate.subject, previewVars)}
                </p>
              </div>
            )}
            <div>
              <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-wide mb-1">
                Body (text)
              </p>
              <pre className="whitespace-pre-wrap text-xs text-zinc-700 bg-zinc-50 rounded-xl px-4 py-3 leading-relaxed font-sans max-h-64 overflow-auto">
                {renderPreview(previewTemplate.bodyText ?? '', previewVars)}
              </pre>
            </div>
            {previewTemplate.bodyHtml && (
              <div>
                <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-wide mb-1">
                  Body (HTML preview)
                </p>
                <div
                  className="text-sm border border-zinc-200 rounded-xl px-4 py-3 max-h-64 overflow-auto"
                  dangerouslySetInnerHTML={{
                    __html: renderPreview(previewTemplate.bodyHtml, previewVars),
                  }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Archive confirm */}
      <ConfirmDialog
        open={!!archiveTarget}
        title="Archive template?"
        message={`"${archiveTarget?.name}" will be archived. Active campaigns using it won't be affected, but new campaigns can't select it.`}
        confirmLabel="Archive"
        variant="warning"
        loading={archiveMutation.isPending}
        onConfirm={() => archiveTarget && archiveMutation.mutate(archiveTarget.id)}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
}

// ─── Shared form fields ───────────────────────────────────────────────────────

function TemplateFormFields({
  form,
  setForm,
  lockChannel = false,
}: {
  form: TemplateForm;
  setForm: React.Dispatch<React.SetStateAction<TemplateForm>>;
  lockChannel?: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Welcome email"
        />
        <Select
          label="Channel"
          value={form.channel}
          onChange={(e) =>
            setForm((f) => ({ ...f, channel: e.target.value as Template['channel'] }))
          }
          disabled={lockChannel}
        >
          <option value="email">Email</option>
          <option value="sms">SMS</option>
          <option value="push">Push</option>
          <option value="webhook">Webhook</option>
        </Select>
      </div>
      {form.channel === 'email' && (
        <Input
          label="Subject"
          value={form.subject}
          onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
          placeholder="Hello {{firstName}}!"
        />
      )}
      <Textarea
        label={form.channel === 'email' ? 'Body (plain text)' : 'Message body'}
        rows={4}
        value={form.bodyText}
        onChange={(e) => setForm((f) => ({ ...f, bodyText: e.target.value }))}
        placeholder="Hi {{firstName}}, ..."
      />
      {form.channel === 'email' && (
        <Textarea
          label="Body (HTML) — optional"
          rows={4}
          value={form.bodyHtml}
          onChange={(e) => setForm((f) => ({ ...f, bodyHtml: e.target.value }))}
          placeholder="<p>Hi {{firstName}}</p>"
        />
      )}
    </div>
  );
}
